import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth, type Role } from '../context/AuthContext'
import { useTimeCheckIn } from '../hooks/useTimeCheckIn'
import { Sparkles } from 'lucide-react'

function FullScreenLoader() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-cream-50">
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-10 w-10 animate-pulse items-center justify-center rounded-xl bg-brand-500 text-white">
          <Sparkles size={18} strokeWidth={2.5} />
        </div>
        <p className="text-sm text-canvas-fg/60">Loading...</p>

      </div>
    </div>
  )
}

/**
 * Client-side route guard for UX only (instant redirect, no flash of
 * protected content). This is NOT the security boundary — every API call
 * behind these routes is independently authorized server-side, and the
 * server never trusts anything the client claims about who's logged in.
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading, currentUser } = useAuth()
  const location = useLocation()
  // Every signed-in screen, write mode and the canvas included, counts
  // towards time in Hapi. A service token is not somebody, so it does not.
  useTimeCheckIn(isAuthenticated && currentUser !== null && !currentUser.service)

  if (isLoading) return <FullScreenLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <Outlet />
}

export function RequireRole({ roles }: { roles: Role[] }) {
  const { isAuthenticated, isLoading, hasRole } = useAuth()
  const location = useLocation()

  if (isLoading) return <FullScreenLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  if (!hasRole(...roles)) return <Navigate to="/today" replace />
  return <Outlet />
}
